// The admin account page's two reminder message boxes (SMS and email) save when the
// admin leaves the box. The rules for what counts as text typed and not yet saved
// live here, apart from React, so tests/client/message-draft.test.ts can pin them.
// The React half is client/src/components/account-message-draft.tsx.

/** The text a message box was last filled with from the server, and whose account it was. */
export type MessageFill = { accountId: number; text: string };

export type MessageSaveStatus = "idle" | "saving" | "saved" | "error";

/** The line under the box. */
export type MessageHint = "none" | "unsaved" | "saving" | "saved" | "failed";

type Typed = string | null | undefined;

/**
 * True when the box holds text the admin typed that differs from what the server
 * holds for THIS account. A box still showing another account's text (the page has
 * moved on and the new account has not been filled in yet) is never "edited" for
 * the new one — its text must not follow the admin to the next account.
 */
export function isMessageEdited(
  typed: Typed,
  filledWith: MessageFill | undefined,
  accountId: number | undefined,
): boolean {
  return filledWith !== undefined && filledWith.accountId === accountId && (typed ?? "") !== filledWith.text;
}

/**
 * What the box shows once the account (or the org's defaults) arrive again. A save
 * anywhere on the page refetches the account; text typed and not yet saved stays.
 */
export function messageAfterRefill(
  typed: Typed,
  filledWith: MessageFill | undefined,
  accountId: number,
  serverText: string,
): string {
  return isMessageEdited(typed, filledWith, accountId) ? (typed ?? "") : serverText;
}

/**
 * True when leaving the box (or the page) has text to send that is not already on
 * its way. The save goes to the account the box was filled for, whatever account
 * the page shows by the time it runs.
 */
export function shouldSaveMessage(
  typed: Typed,
  filledWith: MessageFill | undefined,
  onItsWay: MessageFill | undefined,
): boolean {
  if (!filledWith || !isMessageEdited(typed, filledWith, filledWith.accountId)) return false;
  return !(onItsWay?.accountId === filledWith.accountId && onItsWay.text === (typed ?? ""));
}

export function messageHint(
  typed: Typed,
  filledWith: MessageFill | undefined,
  status: MessageSaveStatus,
): MessageHint {
  if (!filledWith) return "none";
  if ((typed ?? "") === filledWith.text) return status === "saved" ? "saved" : "none";
  if (status === "saving") return "saving";
  if (status === "error") return "failed";
  return "unsaved";
}
